import React from "react";

import EmployeeCoverageDumb from "EmployeeCoverage/EmployeeCoverage";
import EmployeeMECPlan from "EmployeeCoverage/EmployeeMECPlan";
import isIntuit from "Intuit/isIntuit";
import Spinner from "Intuit/Spinner";
import { isTrowserRollout } from "Intuit/TrowserUtils";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import employeeHashId from "Utils/employeeHashID";
import BasicLoading from "ZeroRuby/BasicLoading";

import { SurfaceHeader, SurfaceSection } from "Cobalt/Surface";
import AgentHeader from "Common/Agent/AgentHeader";
import BodyText from "Common/BodyText";
import { RoutedBackButton } from "Common/forms/buttons";
import Icon from "Common/Icon";

import { useGetApplicationQuery, useGetOfferedPlansQuery, getEmployeeForm, getEmployeeApplication, getCompanyContribution, getAdminMode } from "./api";
import Page from "./Page";
import { applyContributionTweaks } from "./selectors";
import { basicInformationURL, isRenewal } from "./urls";

const defaultMessage = (
  <BodyText componentClass="p" size="large" agent style={{ textAlign: "center", paddingTop: 50 }}>
    You must enter your home zip code before selecting a plan.
  </BodyText>
);

const employeeBasicInformationLink = (url: string) => (
  <BodyText componentClass="p" size="large" agent style={{ textAlign: "center" }}>
    <Link to={url}>Click here to enter your home zip code.</Link>
  </BodyText>
);

const LoadingCard = ({ type, backLink, onClickBack }) => (
  <SurfaceSection>
    <BodyText size="large" style={{ textAlign: "center", paddingTop: 50 }} agent>
      Loading your {type} plan options now&hellip;
    </BodyText>
    <div style={{ textAlign: "center", padding: 50 }}>{isIntuit() && isTrowserRollout() ? <Spinner mx="auto" /> : <Icon icon="spinner" spin size="3x" />}</div>
    <RoutedBackButton href={backLink} onClick={onClickBack} />
  </SurfaceSection>
);

const EmployeeCoverageContent = ({ type, backLink, onClickBack }) => {
  const employeePolicy = useSelector(getEmployeeApplication);
  const employeeForm = useSelector(getEmployeeForm);
  const isAdmin = useSelector(getAdminMode);

  const { data: offeredPlans, isLoading } = useGetOfferedPlansQuery({
    type,
    id: employeePolicy?.id,
  });

  const prices = offeredPlans?.prices;
  const plansObject = offeredPlans?.plans;
  const plans = prices ? prices.map((price) => plansObject[price.planId]) : [];
  const contributions = applyContributionTweaks(type, useSelector(getCompanyContribution));

  if (isLoading) {
    return <LoadingCard type={type} backLink={backLink} onClickBack={onClickBack} />;
  }
  if (!employeeForm?.zipCode) {
    return (
      <SurfaceSection>
        {defaultMessage}
        {employeeBasicInformationLink(basicInformationURL(isAdmin, isRenewal(), employeeHashId()))}

        <RoutedBackButton href={backLink} onClick={onClickBack} />
      </SurfaceSection>
    );
  }
  if (plans.length === 0) {
    return (
      <SurfaceSection>
        <BodyText size="large" agent>
          Your company does not offer {type} insurance at this time.
        </BodyText>
        <RoutedBackButton href={backLink} onClick={onClickBack} />
      </SurfaceSection>
    );
  }
  if (plans[0].type == "medical" && plans[0].target_market == "mec") {
    return <EmployeeMECPlan plan={plans[0]} prices={prices[0]} contributions={contributions} backLink={backLink} onClickBack={onClickBack} />;
  }
  return (
    <>
      <SurfaceHeader>Your {type.charAt(0).toUpperCase() + type.slice(1)} Plan Choices</SurfaceHeader>
      <SurfaceSection>
        <AgentHeader>Choose the {type} coverage that best fits your needs.</AgentHeader>
      </SurfaceSection>
      <SurfaceSection>
        <EmployeeCoverageDumb plans={plans} prices={prices} contributions={contributions} backLink={backLink} onClickBack={onClickBack} />
      </SurfaceSection>
    </>
  );
};
/**
 * This page has several states:
 * 1. Loading - while fetching the application
 * 2. Loading Plans - while fetching the offered plans
 * 3. No zip code - if the user hasn't entered their zip code
 * 4. No plans - if the company doesn't offer the selected coverage
 * 5. Plans available
 *    a. MEC Plan - if the plan is a Medical MEC plan, then we render a specific component.
 *    b. EmployeeCoverage component - default component for all supported coverage types.
 */
export default ({ type, backLink, onClickBack }) => {
  const { isLoading } = useGetApplicationQuery();
  return (
    <Page title={`Choose ${type} coverage`}>
      {isLoading ? <BasicLoading /> : <EmployeeCoverageContent type={type} backLink={backLink} onClickBack={onClickBack} key={type} />}
    </Page>
  );
};
